import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Mail,
  Globe,
  MapPin,
  Users,
  ShieldCheck,
  BarChart3,
  PieChart,
  RefreshCw,
} from "lucide-react";
import { Badge, Button, Loader, cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { ApiError } from "../../../lib/api";
import {
  getEntity,
  refreshDetail,
  statusVariant,
  type ArDetail,
} from "../../../lib/ariregister";
import { webHref } from "../../../lib/webUrl";
import { PhoneLink } from "../../telephony/PhoneLink";
import { AiTestCallButton } from "../../telephony/aiTestCall";
import { CrmActions } from "./CrmActions";
import { Card, fmtDate as date } from "./detailCard";
import { DebtCard } from "./DebtCard";
import { LicenceCard } from "./LicenceCard";
import { RulingsCard } from "./RulingsCard";
import { FinancialProfile } from "./financial";
import { BrainCard } from "./brain";
import { DomainCheckCard } from "./domain";
import { SiteScanCard } from "./scan";
import { WebNeedCard } from "./webneed";
import { ProcurementCard } from "./procurement";

function eur(cents: number | null | undefined, currency = "EUR"): string {
  if (cents == null) return "-";
  return new Intl.NumberFormat("et-EE", { style: "currency", currency }).format(cents / 100);
}
/** Birth date + age "02.10.1988 (37)". Open data carries it for only some people
 *  (mostly foreigners) - otherwise "-". For holders of an Estonian personal code
 *  RIK does not publish it for free (only the hash). */
function birth(s: string | null | undefined): string {
  if (!s) return "-";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "-";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return `${d.toLocaleDateString("et-EE")} (${age})`;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/**
 * The registered website.
 *
 * The text is the register's own value, unedited - this is the detail view and
 * the raw field is the point. Only the href is repaired: most rows carry no
 * scheme, and a scheme-less href is a relative path, so the link would resolve
 * against the CRM. A value with no site in it (an e-mail address, a note) stays
 * plain text rather than becoming a link that goes nowhere.
 */
function WebValue({ webUrl }: { webUrl: string | null }) {
  if (!webUrl) return <>-</>;
  const href = webHref(webUrl);
  if (!href) return <span className="inline-flex items-center gap-1"><Globe className="size-3" />{webUrl}</span>;
  return (
    <a className="inline-flex items-center gap-1 text-primary hover:underline" href={href} target="_blank" rel="noreferrer">
      <Globe className="size-3" />{webUrl}
    </a>
  );
}

export function AriregisterDetail({ user }: { user: StaffUser }) {
  const t = useT();
  const { regCode = "" } = useParams();
  const [entity, setEntity] = useState<ArDetail | null | "loading">("loading");
  const [notFound, setNotFound] = useState(false);
  const [refreshState, setRefreshState] = useState<"idle" | "busy" | "done" | string>("idle");
  const canManage = userCanPlatform(user, "manage:ariregister");
  const canViewCrm = userCanPlatform(user, "view:crm");
  const canManageClients = userCanPlatform(user, "manage:crm.clients");
  // The AI test call's own key, checked before the button asks for anything.
  const canAiCall = userCanPlatform(user, "manage:crm.calls");

  useEffect(() => {
    let alive = true;
    setEntity("loading");
    setNotFound(false);
    getEntity(regCode)
      .then((e) => alive && setEntity(e))
      .catch((err) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 404) setNotFound(true);
        setEntity(null);
      });
    return () => {
      alive = false;
    };
  }, [regCode]);

  async function doRefresh() {
    setRefreshState("busy");
    try {
      await refreshDetail(regCode);
      // Reload the updated entity (email/phone/commChannels/detailFetchedAt).
      const updated = await getEntity(regCode);
      setEntity(updated);
      setRefreshState("done");
      setTimeout(() => setRefreshState("idle"), 3000);
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { message?: string }) : null;
      setRefreshState(body?.message || t("ariregister.detail.refreshFailed"));
    }
  }

  if (entity === "loading") {
    return (
      <div className="flex justify-center py-20">
        <Loader size="lg" />
      </div>
    );
  }

  if (notFound || !entity) {
    return (
      <div className="space-y-4">
        <BackLink t={t} />
        <div className="rounded-xl border border-dashed border-border bg-card/60 px-6 py-14 text-center text-sm text-muted-foreground shadow-soft">
          {t("ariregister.detail.notFound", { regCode })}
        </div>
      </div>
    );
  }

  const e = entity;
  const acts = e.activities ?? [];

  return (
    <div className="space-y-5">
      <BackLink t={t} />

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">{e.name}</h1>
            {e.status && <Badge variant={statusVariant(e.status)}>{e.statusText || e.status}</Badge>}
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-muted-foreground">
            <span>{t("ariregister.col.regCode")}: <span className="font-mono text-foreground">{e.regCode}</span></span>
            {e.legalForm && <span>· {e.legalForm}</span>}
            {e.vatNumber && <span>· {t("ariregister.col.vat")}: <span className="font-mono text-foreground">{e.vatNumber}</span></span>}
          </p>
        </div>
        {/* Already a lead, already a client, or neither - and the button that
            fits the answer. */}
        <CrmActions entity={e} t={t} canView={canViewCrm} canManage={canManageClients} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── Contact ─────────────────────────────────────────────────────── */}
        <Card title={t("ariregister.detail.contact")} icon={Mail}>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t("ariregister.detail.email")}>
              {e.email ? <a className="text-primary hover:underline" href={`mailto:${e.email}`}>{e.email}</a> : "-"}
            </Field>
            <Field label={t("ariregister.detail.phone")}>
              {e.phone ? (
                <PhoneLink phone={e.phone} source="ariregister" companyName={e.name} regCode={e.regCode} />
              ) : "-"}
            </Field>
            <Field label={t("ariregister.detail.web")}>
              <WebValue webUrl={e.webUrl} />
            </Field>
            <Field label={t("ariregister.detail.address")}>
              <span className="inline-flex items-start gap-1"><MapPin className="mt-0.5 size-3 shrink-0" />{e.addressFull || "-"}</span>
            </Field>
          </dl>
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3">
            {canManage && (
              <Button variant="ghost" size="sm" onClick={doRefresh} disabled={refreshState === "busy"}>
                <RefreshCw className={cn("size-3.5", refreshState === "busy" && "animate-spin")} />
                {t("ariregister.detail.refresh")}
              </Button>
            )}
            <span className="text-xs text-muted-foreground">
              {e.detailFetchedAt
                ? t("ariregister.detail.lastDetail", { date: date(e.detailFetchedAt) })
                : t("ariregister.detail.mirrorOnly")}
            </span>
            {canAiCall && (
              <AiTestCallButton
                target={{ regCode: e.regCode, name: e.name }}
                className="flex min-w-0 flex-wrap items-center gap-2 sm:ml-auto"
              />
            )}
          </div>
          {refreshState === "done" && (
            <p className="mt-2 rounded-md bg-green-500/10 px-3 py-2 text-xs text-green-700 dark:text-green-400">
              {t("ariregister.detail.refreshDone")}
            </p>
          )}
          {typeof refreshState === "string" && refreshState !== "idle" && refreshState !== "busy" && refreshState !== "done" && (
            <p className="mt-2 rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">{refreshState}</p>
          )}
        </Card>

        {/* ── General data ────────────────────────────────────────────────── */}
        <Card title={t("ariregister.detail.general")} icon={Building2}>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t("ariregister.detail.legalForm")}>{e.legalForm || "-"}{e.legalFormSub ? ` · ${e.legalFormSub}` : ""}</Field>
            <Field label={t("ariregister.detail.capital")}>{eur(e.capitalCents, e.capitalCurrency || "EUR")}</Field>
            <Field label={t("ariregister.detail.registered")}>{date(e.registeredAt)}</Field>
            <Field label={t("ariregister.detail.county")}>{e.county || "-"}</Field>
            <Field label={t("ariregister.detail.mainActivity")}>
              {e.emtakText ? <>{e.emtakText}{e.emtak ? <span className="text-muted-foreground"> ({e.emtak})</span> : null}</> : "-"}
            </Field>
            <Field label={t("ariregister.detail.synced")}>{date(e.syncedAt)}</Field>
          </dl>
        </Card>
      </div>

      {/* ── Why this company ranks where it does in the prospecting list ───── */}
      <WebNeedCard regCode={e.regCode} />

      {/* ── Domain and DNS (sales intel: age + current providers) ─────────── */}
      <DomainCheckCard regCode={e.regCode} webUrl={e.webUrl} t={t} />

      {/* ── The website itself: platform, age of it, defects, contacts ────── */}
      <SiteScanCard regCode={e.regCode} webUrl={e.webUrl} t={t} />

      {/* ── What is wrong with it, in the owner's terms, with proof ──────── */}
      {/* The company card above already offers the AI test call. */}
      <BrainCard domain={e.webUrl} canManage={canManage} me={user.id} t={t} aiTestCall={false} />

      {/* ── Tax arrears. Above the financial profile on purpose: it disqualifies
             a prospect, so it must not sit below three charts. Absent when the
             company is not on the newest EMTA list, which is not the same as a
             clean bill of health - see the card. ─────────────────────────── */}
      <DebtCard e={e} t={t} />

      {/* ── What the state has PERMITTED, as opposed to the EMTAK the company
             reported about itself. Absent when MTR lists nothing. ────────── */}
      <LicenceCard licences={e.licences ?? []} t={t} />

      {/* ── What the company has SOLD to the public sector: the procurements
             it won (RHR). Absent when the mirror holds no win. ────────────── */}
      <ProcurementCard e={e} t={t} />

      {/* ── Financial profile (wealth: EMTA + reports + revenue by activity) ── */}
      <FinancialProfile e={e} t={t} />

      {/* ── Activities ────────────────────────────────────────────────────── */}
      {acts.length > 0 && (
        <Card title={t("ariregister.detail.activities")} icon={BarChart3}>
          <ul className="space-y-1.5">
            {acts.map((a, i) => (
              <li key={`${a.emtak_kood}-${i}`} className="flex items-center gap-2 text-sm">
                {a.on_pohitegevusala && <Badge variant="default" className="shrink-0">{t("ariregister.detail.primary")}</Badge>}
                <span className="font-mono text-xs text-muted-foreground">{a.emtak_kood}</span>
                <span>{a.emtak_tekstina}</span>
                {a.nace_kood && <span className="text-xs text-muted-foreground">· NACE {a.nace_kood}</span>}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ── Board / persons ───────────────────────────────────────────────── */}
      {e.persons.length > 0 && (
        <Card title={t("ariregister.detail.persons")} icon={Users}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.personName")}</th>
                  <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.birthDate")}</th>
                  <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.role")}</th>
                  <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.reprRights")}</th>
                  <th className="py-1.5 font-medium">{t("ariregister.detail.from")}</th>
                </tr>
              </thead>
              <tbody>
                {e.persons.map((p) => (
                  <tr key={p.id} className={cn("border-b border-border/50", p.endDate && "text-muted-foreground")}>
                    <td className={cn("py-1.5 pr-3 font-medium", p.endDate && "line-through")}>
                      <PersonName name={p.personName} hash={p.personCodeHash} linkedRegCode={p.linkedRegCode} />
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums text-muted-foreground">{birth(p.birthDate)}</td>
                    <td className="py-1.5 pr-3">{p.role}</td>
                    <td className="py-1.5 pr-3">{p.reprRights || "-"}</td>
                    <td className="py-1.5">{date(p.startDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Shareholders ──────────────────────────────────────────────────── */}
      {e.shareholders.length > 0 && (
        <Card title={t("ariregister.detail.shareholders")} icon={PieChart}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.personName")}</th>
                  <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.birthDate")}</th>
                  <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.share")}</th>
                  <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.ownershipType")}</th>
                  <th className="py-1.5 font-medium">{t("ariregister.detail.from")}</th>
                </tr>
              </thead>
              <tbody>
                {e.shareholders.map((s) => (
                  <tr key={s.id} className={cn("border-b border-border/50", s.endDate && "text-muted-foreground")}>
                    <td className={cn("py-1.5 pr-3 font-medium", s.endDate && "line-through")}>
                      <PersonName name={s.ownerName} hash={s.personCodeHash} linkedRegCode={s.linkedRegCode} />
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums text-muted-foreground">{birth(s.birthDate)}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{s.sharePercent ? `${s.sharePercent}%` : "-"}</td>
                    <td className="py-1.5 pr-3 text-muted-foreground">{s.ownershipType || "-"}</td>
                    <td className="py-1.5">{date(s.startDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Rulings and registry entries (the registry-file history) ──────── */}
      <RulingsCard rulings={e.rulings} t={t} />

      {/* ── Beneficial owners ─────────────────────────────────────────────── */}
      {e.beneficials.length > 0 && (
        <Card title={t("ariregister.detail.beneficials")} icon={ShieldCheck}>
          <ul className="space-y-1.5 text-sm">
            {e.beneficials.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center gap-x-2">
                <span className="font-medium">{b.ownerName}</span>
                {b.controlType && <span className="text-xs text-muted-foreground">· {b.controlType}</span>}
                {b.residenceCountry && <span className="text-xs text-muted-foreground">· {b.residenceCountry}</span>}
              </li>
            ))}
          </ul>
        </Card>
      )}

    </div>
  );
}

function BackLink({ t }: { t: (k: string) => string }) {
  return (
    <Link to="/ariregister" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-4" /> {t("ariregister.detail.back")}
    </Link>
  );
}

/** Person/owner name: natural person (hash) -> cross view; legal person (regCode) -> company. */
function PersonName({ name, hash, linkedRegCode }: { name: string; hash: string | null; linkedRegCode: string | null }) {
  if (linkedRegCode) {
    return <Link to={`/ariregister/${linkedRegCode}`} className="text-primary hover:underline">{name}</Link>;
  }
  if (hash) {
    return <Link to={`/ariregister/isik/${hash}`} className="text-primary hover:underline">{name}</Link>;
  }
  return <span>{name}</span>;
}
